import { ScoreFormulaValidationRequest, ScoreFormulaValidationResponse } from "types/scores";
import { Scorecard, ScorecardRow } from "types";
import { ScorecardsAPI, ScoresAPI } from "api";
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const queryKeys = {
  all: () => ["scorecards"],
  details: () => [...queryKeys.all(), "detail"] as const,
  detail: (id: string) => [...queryKeys.details(), id] as const,
  /**
   * This key can invalidate cache for ALL rows of Scorecard Results.
   */
  resultRows: (id: string) => [...queryKeys.detail(id), "results"] as const,
  resultRowSingle: (id: string, row: ScorecardRow) =>
    [
      ...queryKeys.resultRows(id),
      row.rowType === "question_score"
        ? { rowType: row.rowType, questionRefid: row.questionRefid, scoreUri: row.scoreUri }
        : { rowType: row.rowType, scoreRefId: row.scoreRefid },
    ] as const,
};

/**
 * Fetches display options for question scores.
 */
export const useScorecard = <T = Scorecard>(uuid: string, options: UseQueryOptions<Scorecard, unknown, T> = {}) =>
  useQuery<Scorecard, unknown, T>(queryKeys.detail(uuid), () => ScorecardsAPI.get(uuid), options);

/**
 * Create a new Scorecard.
 */
export const useScorecardCreate = (options: UseMutationOptions<Scorecard, unknown, Scorecard> = {}) => {
  return useMutation((data) => ScorecardsAPI.create(data), options);
};

/**
 * Updates an existing Scorecard.
 *
 * NOTE: This automatically updates the cache on success.
 */
export const useScorecardUpdate = (options: UseMutationOptions<Scorecard, unknown, Scorecard> = {}) => {
  const queryClient = useQueryClient();
  return useMutation((data) => ScorecardsAPI.update(data), {
    ...options,
    onSuccess(data, variables, context) {
      queryClient.setQueryData(queryKeys.detail(data.uuid), data);
      /**
       * Remove all cached results on a successful save.
       * We might be able to be more selective here, but safest route is to re-fetch all results.
       */
      queryClient.removeQueries(queryKeys.resultRows(data.uuid));
      return options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Validates a Scorecard formula.
 */
export const useScorecardValidateFormula = (
  options: UseMutationOptions<ScoreFormulaValidationResponse, unknown, ScoreFormulaValidationRequest> = {},
) => {
  return useMutation((data) => ScoresAPI.validate(data), options);
};

/**
 * Deletes a Scorecard.
 */
export const useScorecardDelete = (options: UseMutationOptions<void, unknown, string> = {}) => {
  return useMutation((uuid) => ScorecardsAPI.delete(uuid), options);
};
